import React, { useEffect, useMemo, useState } from 'react';
import { contentApi } from '../../services/api/content.api';
import { LANGUAGES, type GameMode, type Language, type VerbatimRule } from '@business-game/shared';

export const AdminVerbatimsPage: React.FC = () => {
  const [gameMode, setGameMode] = useState<GameMode>('teamlab');
  const [language, setLanguage] = useState<Language>('fr');
  const [typeId, setTypeId] = useState<number>(1);
  const [rules, setRules] = useState<VerbatimRule[]>([]);
  const [newBound, setNewBound] = useState<number>(0);

  const loadData = async () => {
    const data = await contentApi.listVerbatimRules(gameMode, typeId);
    setRules(data);
  };

  useEffect(() => {
    void loadData();
  }, [gameMode, typeId]);

  const rows = useMemo(
    () =>
      rules.map((rule) => ({
        ...rule,
        currentText: rule.translations.find((entry) => entry.language === language)?.text || '',
      })),
    [rules, language]
  );

  const handleCreateRule = async (event: React.FormEvent) => {
    event.preventDefault();
    await contentApi.createVerbatimRule({
      gameMode,
      typeId,
      bound: newBound,
      isActive: true,
    });
    setNewBound(0);
    await loadData();
  };

  return (
    <div className="flex min-h-full flex-col font-sans text-slate-900">
      <main className="flex-grow w-full px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 w-full rounded-[2rem] border border-violet-200 bg-gradient-to-br from-violet-50 via-white to-slate-50 p-6 shadow-sm">
          <div className="space-y-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-indigo-500">Verbatims</div>
            <h1 className="mt-2 text-2xl font-black tracking-tight text-slate-900">Seuils de verbatims</h1>
            <p className="mt-2 text-sm text-slate-500">
              Modifiez les seuils de verbatims et leur traduction par langue.
            </p>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <FilterCard label="Mode">
              <select
                value={gameMode}
                onChange={(event) => setGameMode(event.target.value as GameMode)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                <option value="teamlab">TeamLab</option>
                <option value="leadlab">LeadLab</option>
              </select>
            </FilterCard>

            <FilterCard label="Type">
              <select
                value={typeId}
                onChange={(event) => setTypeId(Number(event.target.value))}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                <option value={1}>Type 1</option>
                <option value={2}>Type 2</option>
                <option value={3}>Type 3</option>
                <option value={4}>Type 4</option>
              </select>
            </FilterCard>

            <FilterCard label="Langue">
              <select
                value={language}
                onChange={(event) => setLanguage(event.target.value as Language)}
                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
              >
                {LANGUAGES.map((entry) => (
                  <option key={entry} value={entry}>
                    {entry}
                  </option>
                ))}
              </select>
            </FilterCard>
          </div>

          <SurfaceCard className="p-4">
            <form onSubmit={handleCreateRule} className="grid gap-4 lg:grid-cols-[180px_auto]">
              <input
                type="number"
                value={newBound}
                onChange={(event) => setNewBound(Number(event.target.value))}
                className="rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
                placeholder="Nouveau seuil"
              />
              <button className="rounded-2xl bg-indigo-600 px-4 py-3 font-semibold text-white shadow-lg shadow-indigo-200 transition-all hover:-translate-y-0.5 hover:bg-indigo-700 active:scale-95">
                Ajouter un seuil
              </button>
            </form>
          </SurfaceCard>

          <div className="space-y-4">
            {rows.map((rule) => (
              <VerbatimRuleCard key={rule.id} rule={rule} language={language} onReload={loadData} />
            ))}
          </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const VerbatimRuleCard: React.FC<{
  rule: VerbatimRule & { currentText: string };
  language: Language;
  onReload: () => Promise<void>;
}> = ({ rule, language, onReload }) => {
  const [bound, setBound] = useState(rule.bound);
  const [isActive, setIsActive] = useState(rule.isActive);
  const [text, setText] = useState(rule.currentText);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBound(rule.bound);
    setIsActive(rule.isActive);
    setText(rule.currentText);
  }, [rule]);

  const save = async () => {
    setSaving(true);
    try {
      await contentApi.updateVerbatimRule(rule.id, {
        bound,
        gameMode: rule.gameMode,
        typeId: rule.typeId,
        isActive,
      });
      await contentApi.upsertVerbatimTranslation({
        verbatimRuleId: rule.id,
        language,
        text,
      });
      await onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <SurfaceCard className="p-5 hover:-translate-y-0.5">
      <div className="grid gap-4 lg:grid-cols-[160px_140px_auto]">
        <input
          type="number"
          value={bound}
          onChange={(event) => setBound(Number(event.target.value))}
          className="rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
        />
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 px-4 py-3">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(event) => setIsActive(event.target.checked)}
            className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm font-medium text-slate-700">Actif</span>
        </label>
        <button
          type="button"
          onClick={() => void save()}
          className="rounded-2xl border border-slate-300 px-4 py-3 text-sm font-semibold text-slate-700 transition-colors hover:border-indigo-300 hover:bg-indigo-50/50 disabled:opacity-50"
          disabled={saving}
        >
          Sauver regle
        </button>
      </div>

      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        className="mt-4 min-h-28 w-full rounded-2xl border border-slate-200 px-4 py-3 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500"
        placeholder="Texte du verbatim"
      />
    </SurfaceCard>
  );
};

const FilterCard: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <SurfaceCard className="p-4">
    <label className="mb-2 block text-xs font-bold uppercase tracking-wider text-slate-500">{label}</label>
    {children}
  </SurfaceCard>
);

const SurfaceCard: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:border-indigo-200 hover:shadow-lg ${className}`.trim()}
  >
    {children}
  </div>
);
